import Image from "next/image";
import Button from "@/components/ui/button";
import Link from "@/components/ui/Link";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

interface GridImageWithBtnProps {
  data?: {
    heading?: {
      text?: string;
      title?: string;
    };
    subheading?: {
      text?: string;
      title?: string;
    };
    text?: {
      text?: string;
    };
    content_collection?: {
      id: string;
      site_content_collections_id: {
        collection: {
          collection: string;
          item: {
            //site_content
            text: string;

            //menu_items
            href: string;
            label: string;

            //images
            alt: string;
            image_file: {
              image: {
                id: string;
                filename_download: string;
              };
            };
          };
        }[];
      };
    }[];
    button?: {
      label?: string;
      href?: string;
    };
  };
}

export const fields = [
  "heading.text",
  "heading.title",

  "subheading.text",
  "subheading.title",

  "text.text",

  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",

  "content_collection.site_content_collections_id.collection.item:site_content.text",

  "content_collection.site_content_collections_id.collection.item:images.alt",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.id",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.filename_download",

  "content_collection.site_content_collections_id.collection.item:menu_items.href",
  "content_collection.site_content_collections_id.collection.item:menu_items.label",

  "button.label",
  "button.href",
] as const;

export default function GridImageWithBtn({ data }: GridImageWithBtnProps) {
  const heading = data?.heading?.title || "";
  const subheading = data?.subheading?.title || "";
  const description = data?.text?.text || "";
  const buttonLabel = data?.button?.label || "";
  const buttonHref = data?.button?.href || "";

  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false,
    align: 'center',
    containScroll: 'trimSnaps'
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [firstImageHeight, setFirstImageHeight] = useState<number | null>(null);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  const renderCard = (collection: any, index: number) => {
    const imageItem =
      collection?.site_content_collections_id?.collection.find(
        (item: any) => item.collection === 'images'
      )?.item;
    const menuItem =
      collection?.site_content_collections_id?.collection.find(
        (item: any) => item.collection === 'menu_items'
      )?.item;
    const siteContentItem =
      collection?.site_content_collections_id?.collection.find(
        (item: any) => item.collection === 'site_content'
      )?.item;
    const imageId = imageItem?.image_file?.image?.id || '';
    const imageFilename =
      imageItem?.image_file?.image?.filename_download || '';
    const imageAlt = imageItem?.alt || '';
    const menuItems = menuItem;

    return (
      <div key={index} className='flex flex-col gap-4 items-center'>
        <div 
          className='relative w-full bg-white rounded-lg overflow-hidden'
          style={{ height: firstImageHeight ? `${firstImageHeight}px` : 'auto' }}
        >
          <Image
            src={getReadableImageUrl(imageId, imageFilename)}
            alt={imageAlt}
            width={800}
            height={600}
            className='w-full h-full object-cover rounded-lg'
            quality={95}
            loading={'lazy'}
            sizes="(max-width: 768px) 100vw, 33vw"
            onLoad={(e) => {
              if (index === 0 && !firstImageHeight) {
                const img = e.target as HTMLImageElement;
                setFirstImageHeight(img.offsetHeight);
              }
            }}
          />
        </div>
        {menuItems?.href && menuItems?.label && (
          <Button variant='outline' className='w-fit' asChild>
            <Link href={menuItems.href}>{menuItems.label}</Link>
          </Button>
        )}
        {siteContentItem?.text && (
          <div
            className='text-base font-light font-aileron tracking-tight'
            dangerouslySetInnerHTML={sanitizeHTML(siteContentItem.text)}
          />
        )}
      </div>
    );
  };

  return (
		<section className='GridImageWithBtn bg-[rgba(177,163,164,0.15)] py-12 md:py-16'>
			<div className='container'>
				<div className='mx-auto text-center mb-12'>
					<div className='font-aileron font-bold uppercase text-sm tracking-[2px] text-black mb-4'>
						{subheading}
					</div>
					<h2 className='mb-4'>
						{heading}
					</h2>
					<div
						dangerouslySetInnerHTML={sanitizeHTML(description)}
					/>
				</div>

				{/* Mobile Slider */}
				<div className='lg:hidden relative'>
					{/* Dots Navigation */}
					{scrollSnaps.length > 1 && (
						<div className='flex justify-center gap-2 mb-6'>
							{scrollSnaps.map((_, index) => (
								<button
									key={index}
									className={`w-2 h-2 rounded-full transition-all ${
										index === selectedIndex
											? 'bg-black w-6'
											: 'bg-gray-400'
									}`}
									onClick={() => scrollTo(index)}
									aria-label={`Go to slide ${index + 1}`}
								/>
							))}
						</div>
					)}

					<div className='overflow-hidden' ref={emblaRef}>
						<div className='flex'>
							{data?.content_collection?.map((collection, index) => (
								<div key={index} className='flex-[0_0_100%] min-w-0 px-4'>
									{renderCard(collection, index)}
								</div>
							))}
						</div>
					</div>
				</div>

				{/* Desktop Grid */}
				<div className='hidden lg:grid grid-cols-3 gap-12 md:gap-16'>
					{data?.content_collection?.map((collection, index) => 
						renderCard(collection, index)
					)}
				</div>

				<div className='flex justify-center mt-12'>
					<Button asChild>
						<Link href={buttonHref}>{buttonLabel}</Link>
					</Button>
				</div>
			</div>
		</section>
	)
}
